import { type ReactElement, useCallback } from 'react'

import toLiteralValueOrNull from '@/utils/toLiteralValueOrNull'

import { VIEWPOINTS, type ViewpointSettings } from '@/queries/constants'
import RPC from '@/client/rpc'

import Form from '@/components/UI/Form/Form'
import type { FormInputType, FormValues } from '@/components/UI/Form/types'
import ModalV3 from '@/components/UI/Modal/ModalV3'

interface ViewpointCollectionModalProps {
  open: boolean
  onClose: () => void
  onSubmit: () => void
  sessionRegistrationId: number
  viewpointSettings: ViewpointSettings
}

export default function ViewpointCollectionModal({
  open,
  onClose,
  onSubmit,
  sessionRegistrationId,
  viewpointSettings,
}: ViewpointCollectionModalProps): ReactElement {
  const formInputs: FormInputType[] = [
    {
      type: 'radio',
      direction: 'vertical',
      label: viewpointSettings?.question ?? '',
      sublabel:
        'Your answer to this question will be used to try and match you with someone with a different viewpoint. No one but you and your discussion partner will know your answer unless you choose to share it.',
      name: 'viewpoint',
      options: [
        { value: 'a', label: viewpointSettings?.viewpoint_a ?? '' },
        { value: 'neutral', label: 'I am neutral or unsure' },
        { value: 'b', label: viewpointSettings?.viewpoint_b ?? '' },
      ],
      required: true,
      size: 6,
    },
  ]

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      if (values.viewpoint == null) {
        return
      }

      const viewpoint = toLiteralValueOrNull(VIEWPOINTS, values.viewpoint)
      if (viewpoint == null) {
        throw new Error('Invalid viewpoint')
      }

      await RPC.submitViewpoint({
        sessionRegistrationId,
        viewpoint,
      })

      onSubmit()
    },
    [sessionRegistrationId, onSubmit]
  )

  return (
    <ModalV3
      size="3xl"
      open={open}
      onClose={onClose}
      title="Share Your Viewpoint"
      body={() => (
        <Form name="ViewpointCollectionForm" blocks={formInputs} onSubmit={handleSubmit} />
      )}
    />
  )
}
